<template>
  <div class="datascience-page">
    <!-- En-tête avec dégradé, sans la navigation en double -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Exploitez les données de votre entreprise</h1>
        <p class="subtitle">Analysez la data pour décider de votre stratégie de croissance</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Quelles solutions data correspondent le mieux à votre secteur d'activité ?</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                Notre équipe réunit data scientists, analystes, 
                développeurs et spécialistes des données avec une expérience multisectorielle. 
                Nous intervenons sur l'ensemble du cycle de traitement des données, 
                de l'analyse à la visualisation, en passant par l'implémentation 
                et le déploiement d'outils dédiés.
              </p><br>
              <p>
                ✓ <strong><i>Analyse prédictive</i></strong> : Anticipez les tendances futures, optimisez les processus décisionnels et prévoyez les comportements des clients ou les évolutions du marché.
              </p><br>
              <p>
                ✓ <strong><i>Segmentation et personnalisation client</i></strong> : Analysez en profondeur les différents segments de clientèle pour personnaliser les offres, améliorer l'expérience client et la fidélisation.
              </p><br>
              <p>
                ✓ <strong><i>Optimisation opérationnelle</i></strong> : Identifiez les inefficacités, réduisez les coûts et améliorez les processus internes grâce à l'analyse des données opérationnelles et à l'automatisation.
              </p>
            </div>
            <div class="youtube-container">
              <iframe
                style="width: 100%; height: 100%; border: none; border-radius: 15px;"
                src="https://www.youtube.com/embed/ZEmF4ytCdfs"
                title="Présentation de nos solutions de Data Science"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen>
              </iframe>
            </div>
          </div>
        </div>
      </section>

      <!-- Dashboard Interactif Illustration -->
      <section class="dashboard-illustration">
        <div class="illustration-container">
          <img src="/img/dashboard.svg" alt="Dashboard interactif avec graphiques et métriques" class="dashboard-image" />
        </div>
      </section>


      <!-- Qu'est-ce que la Data Science -->
      <section class="card what-is-card">
        <div class="card-content">
          <h2 class="section-title">Qu'est-ce que la Data Science ?</h2>
          <div class="text-content">
            <p>
              La Data Science est la discipline qui utilise des méthodes scientifiques, des processus, des 
              algorithmes et des systèmes pour extraire des connaissances et des insights à partir de données 
              structurées et non structurées. En combinant statistiques, analyse de données, apprentissage 
              automatique et techniques connexes, la Data Science permet aux entreprises de prendre des 
              décisions éclairées basées sur des données concrètes plutôt que sur l'intuition. Dans un monde 
              où les données sont générées à un rythme sans précédent, la capacité à les analyser efficacement 
              devient un avantage concurrentiel absolument indispensable.
            </p>
          </div>
        </div>
      </section>

      <!-- Pourquoi choisir nos services de Data Science -->
      <section class="card why-choose-card">
        <div class="card-content">
          <h2 class="section-title">Comment pouvons-nous vous aider à valoriser vos données avec la Data Science ?</h2>
          <div class="text-content">
            <p>
              Nos solutions vous aident à améliorer votre processus décisionnel grâce à une analyse méthodique 
              de vos données. Nous utilisons des technologies adaptées à vos besoins spécifiques pour vous permettre 
              d'exploiter efficacement les informations dont vous disposez. Avec notre approche, vous serez capable 
              d'identifier les opportunités et vous disposerez d'éléments tangibles pour appuyer vos décisions 
              stratégiques.
            </p>
          </div>
        </div>
      </section>

      <!-- Avantages (Cartes) -->
      <div class="advantages-grid">
        <!-- Avantage 1 -->
        <div class="advantage-card">
          <div class="advantage-number">1</div>
          <h3 class="advantage-title">Excel et VBA : La porte d'entrée vers la data analyse et la visualisation</h3>
          <p class="advantage-description">
            Excel est un outil puissant qui sert de pierre angulaire pour l'analyse de données dans diverses industries. 
            Son interface intuitive et ses fonctionnalités éprouvées le rendent accessible à tous. En travaillant avec nous, 
            vous pouvez exploiter tout le potentiel d'Excel grâce à une formation et un support personnalisés. Notre expertise 
            en VBA vous permettra, par exemple, d'automatiser les tâches répétitives et de créer des tableaux de bord interactifs.
          </p>
        </div>

        <!-- Avantage 2 -->
        <div class="advantage-card">
          <div class="advantage-number">2</div>
          <h3 class="advantage-title">SQL et SAS : Élevez votre gestion et analyse de données au niveau supérieur</h3>
          <p class="advantage-description">
            SQL est essentiel pour gérer les gros volumes de data et interroger les bases de données relationnelles. Notre équipe 
            travaillera étroitement avec vous pour démystifier SQL et vous permettre d'effectuer des requêtes 
            complexes. Couplé à SAS, vous aurez accès à des analyses détaillées et des outils de business intelligence 
            qui transformeront vos données en éléments décisionnels.
          </p>
        </div>

        <!-- Avantage 3 -->
        <div class="advantage-card">
          <div class="advantage-number">3</div>
          <h3 class="advantage-title">Python : Votre partenaire de premier plan en Data Science et Data Visualisation</h3>
          <p class="advantage-description">
            Python s'est imposé comme un favori dans la communauté des data sciences. Notre équipe expérimentée 
            vous aidera à exploiter Python pour automatiser des flux de travail complexes, mener des analyses 
            poussées et développer des modèles prédictifs grâce au machine learning. Ensemble, nous élèverons 
            votre planification stratégique et vous donnerons un avantage concurrentiel certain.
          </p>
        </div>

        <!-- Avantage 4 -->
        <div class="advantage-card">
          <div class="advantage-number">4</div>
          <h3 class="advantage-title">Expertise sectorielle diversifiée</h3>
          <p class="advantage-description">
            Nous avons collaboré avec des clients dans divers domaines (startups, e-commerce, santé, 
            industrie, finance, assurance, etc.). Nous comprenons les défis auxquels votre entreprise 
            est confrontée au quotidien et pouvons rapidement nous adapter à vos besoins. Nous implémenterons 
            des solutions sur mesure pour extraire, nettoyer et stocker vos données, puis les analyser en profondeur.
          </p>
        </div>

        <!-- Avantage 5 -->
        <div class="advantage-card">
          <div class="advantage-number">5</div>
          <h3 class="advantage-title">Développement durable et environnement</h3>
          <p class="advantage-description">
            Nous avons également travaillé sur des projets liés à l'environnement, y compris des évaluations 
            carbone et la consommation d'énergie. Notre équipe peut donc vous aider à établir votre bilan carbone 
            et à renforcer votre plan RSE. Vous pourrez ainsi répondre aux enjeux 
            auxquels fait face votre entreprise en matière de développement durable, avoir  
            un impact positif et une meilleure image de marque, tout en atteignant vos objectifs environnementaux.
          </p>
        </div>

        <!-- Avantage 6 -->
        <div class="advantage-card">
          <div class="advantage-number">6</div>
          <h3 class="advantage-title">Analyses et rapports détaillés</h3>
          <p class="advantage-description">
            La Data Science, c'est aussi la création de tableaux de bord, la gestion de projets de données, 
            le conseil stratégique en données, l'analyse de données (Data Mining, Data Science, Machine Learning) 
            et le support sur divers outils et langages (Excel, SQL, Python, SAS, VBA, R). Vous aurez accès à  
            des rapports clairs, avec des solutions de visualisation pratiques et faciles à lire. De cette manière, 
            vous disposerez d'éléments décisionnels directement exploitables.
          </p>
        </div>
      </div>

      <!-- Témoignages clients -->
      <section class="testimonials-section">
        <h2 class="testimonials-title">Ce que disent nos clients</h2>
        
        <div class="testimonials-container">
          <!-- Témoignage 1 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Marie D.</h3>
                <p class="client-company">Directrice pôle développement</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>L'expertise d'Arynae en analyse de données nous a permis d'identifier des opportunités 
              commerciales inexploitées et d'augmenter notre ROI de 28% en six mois. Leur approche 
              méthodique et leurs insights nous ont donné un avantage concurrentiel significatif.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Témoignage 2 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Pierre L.</h3>
                <p class="client-company">CEO</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>Le bilan carbone réalisé par Arynae a transformé notre approche de la durabilité. 
              Leurs analyses détaillées nous ont permis d'identifier les principaux domaines d'amélioration 
              et de réduire notre empreinte environnementale de 40% en un an.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Témoignage 3 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Sophie N.</h3>
                <p class="client-company">Directrice logistique</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>Les tableaux de bord personnalisés développés par Arynae ont révolutionné notre 
              prise de décision. Nous avons maintenant une visibilité en temps réel sur nos 
              performances et pouvons réagir rapidement aux tendances du marché.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
        </div>
        

      </section>

      <!-- Call to action -->
      <div class="cta-container">
        <span class="cta-button fake-link" @click="navigateTo('e1')">Demandez une consultation</span>
      </div>
    </div>
  </div>
</template>

  <script>
  import { useHead } from '@vueuse/head'
  export default {
  name: 'DataSciencePage',
  setup() {
      useHead({
      title: 'Services de Data Science - Solutions d\'analyse de données | Arynae',
      meta: [
          { name: 'description', content: 'Services B2B de Data Science et d\'analyse de données. Transformez vos données en indicateurs opérationnels pour la prise de décision et gagnez un avantage concurrentiel.' },
          { name: 'keywords', content: 'data science, analyse de données, big data, business intelligence, tableau de bord, KPI, indicateurs, data visualisation, python, excel, vba, sql, sas, machine learning, Arynae' },
          { name: 'robots', content: 'index, follow' },
          { name: 'author', content: 'Arynae' },
          { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
          // Open Graph essentiels
          { property: 'og:type', content: 'website' },
          { property: 'og:title', content: 'Services de Data Science - Solutions d\'analyse de données | Arynae' },
          { property: 'og:description', content: 'Services B2B de Data Science et d\'analyse de données. Transformez vos données en indicateurs opérationnels pour la prise de décision et gagnez un avantage concurrentiel.' },
          { property: 'og:url', content: 'https://arynae.com/datascience' },
          { property: 'og:locale', content: 'fr_FR' },
          { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
          { property: 'og:image:alt', content:'Agence digitale - Solutions No Code, SaaS, Chatbots & SEO' },
          // Twitter Card basiques
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: 'Services de Data Science - Solutions d\'analyse de données | Arynae' },
          { name: 'twitter:description', content: 'Services B2B de Data Science et d\'analyse de données. Transformez vos données en indicateurs opérationnels pour la prise de décision et gagnez un avantage concurrentiel.' },
          { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
          { rel: 'canonical', href: 'https://arynae.com/datascience' },
          { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/datascience' }
      ],
      htmlAttrs: {
          lang: 'fr'
      }
      })
      
      return {}
  },
  data() {
    return {}
  },
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    }
  }
}
  </script>


<style scoped>
.datascience-page {
font-family: Arial, sans-serif;
color: #333;
}

/* Header Styles */
.header {
background: linear-gradient(to right, #2563EB, #7C3AED);
padding-bottom: 6rem;
color: white;
}

.header-content {
text-align: center;
padding: 5rem 2rem 3rem; /* Augmenté le padding-top pour compenser l'absence de la navigation */
}

.main-title {
font-size: 2.75rem;
margin-bottom: 1rem;
}

.subtitle {
font-size: 1.25rem;
max-width: 800px;
margin: 0 auto;
}

/* Content Container */
.content-container {
max-width: 1200px;
margin: -3rem auto 4rem;
padding: 0 2rem;
}

/* Card Styles */
.card {
background: linear-gradient(to bottom, #ffffff, #f3f4f6);
border-radius: 0.5rem;
box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
margin-bottom: 2rem;
overflow: hidden;
}

.card-content {
padding: 2rem;
}

.section-title {
font-size: 1.75rem;
margin-bottom: 1.5rem;
color: #333;
display: block; /* Assure que le titre occupe toute la largeur */
}

.text-content {
font-size: 1rem;
line-height: 1.6;
color: #555;
}

/* Intro Card with Illustration */
.text-and-illustration {
display: flex;
flex-wrap: wrap;
gap: 2rem;
}

.text-and-illustration .text-content {
flex: 2;
min-width: 300px;
}

/* Conteneur YouTube et vidéo */
.youtube-container {
flex: 1;
min-width: 300px;
height: 250px; /* Hauteur fixe pour le conteneur */
display: flex;
justify-content: center;
align-items: center;
}

/* Advantages Grid */
.advantages-grid {
display: grid;
grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
gap: 2rem;
margin: 3rem 0;
}

.advantage-card {
background: white;
border-radius: 0.5rem;
box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
padding: 2rem;
position: relative;
}

.advantage-number {
display: flex;
justify-content: center;
align-items: center;
width: 60px;
height: 60px;
border-radius: 50%;
background: linear-gradient(to right, #2563EB, #7C3AED);
color: white;
font-size: 1.25rem;
font-weight: bold;
margin-bottom: 1rem;
}

.advantage-title {
font-size: 1.25rem;
margin-bottom: 1rem;
color: #333;
}

.advantage-description {
font-size: 0.875rem;
line-height: 1.6;
color: #555;
}

/* Testimonials Section */
/* Testimonials Section */
.testimonials-container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  padding: 1rem;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  padding-bottom: 1rem; /* Espace pour la scrollbar */
}

.testimonial-card {
  flex: 0 0 calc(33.333% - 1.5rem);
  min-width: 280px;
  background: white;
  border-radius: 16px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Cache la scrollbar sur certains navigateurs mais garde la fonctionnalité */
.testimonials-container::-webkit-scrollbar {
  height: 6px;
}

.testimonials-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.testimonials-container::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .testimonials-container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.dots-navigation {
  display: none;
}

.dots-navigation {
  display: none; /* Cache les points s'ils ne sont pas nécessaires */
}

@media (max-width: 992px) {
  .testimonials-container {
    flex-direction: column;
    padding: 1rem;
    gap: 2rem;
  }
  
  .testimonial-card {
    width: 100%;
    margin-bottom: 1rem;
  }
}

.testimonial-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 0 12px rgba(0, 0, 0, 0.06);
}

.testimonial-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 1.5rem 0.5rem;
}

.client-info {
  flex: 1;
}

.client-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
  color: #1a202c;
}

.client-company {
  font-size: 0.9rem;
  color: #718096;
  margin: 0 0 0.5rem;
}

.rating {
  display: flex;
  font-size: 1.1rem;
  color: #FFC107;
}

.testimonial-body {
  padding: 0.5rem 1.5rem 2rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #4A5568;
  flex: 1;
}

.testimonial-border {
  height: 5px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  width: 100%;
}


.testimonials-section {
  padding: 2rem 0;
  margin: 2rem 0;
  position: relative;
}

.testimonials-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;
  position: relative;
  z-index: 1;
}

/* Call to Action */
.cta-container {
text-align: center;
margin: 3rem 0;
}

.cta-button {
background: linear-gradient(to right, #2563EB, #7C3AED);
color: white;
font-size: 1.25rem;
font-weight: bold;
padding: 1rem 2.5rem;
border: none;
border-radius: 2rem;
cursor: pointer;
box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
transition: transform 0.3s ease, box-shadow 0.3s ease;
text-decoration: none;
display: inline-block;
}

.cta-button:hover {
transform: translateY(-2px);
box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .header-content {
    padding: 4rem 1rem 2rem;
  }

  .main-title {
    font-size: 2rem;
  }

  .content-container {
    padding: 0 1rem;
  }

  .advantages-grid {
    grid-template-columns: 1fr;
  }

  .text-and-illustration {
    flex-direction: column;
  }

  .youtube-container {
    height: 200px; /* Hauteur réduite sur mobile */
  }
  
  /* Intro Card - Fix pour la section d'introduction */
  .intro-card .text-and-illustration {
    flex-direction: column;
  }
  
  .intro-card .text-content {
    width: 100%;
    min-width: 100%;
    margin-bottom: 1rem;
  }
  
  .intro-card .youtube-container {
    width: 100%;
    min-width: 100%;
    margin-top: 1rem;
  }
  
  .intro-card .section-title {
    font-size: 1.5rem;
    text-align: center;
  }

  .testimonials-container {
    padding: 1rem;
    flex-direction: column;
    gap: 2rem;
  }

  .testimonial-card {
    min-width: 100%;
  }

  .testimonials-title {
    font-size: 2rem;
    margin-bottom: 2.5rem;
  }
}


/* Dashboard Illustration */
.dashboard-illustration {
  margin: 2rem 0 3rem;
}

.illustration-container {
  display: flex;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
}

.dashboard-image {
  width: 100%;
  max-width: 800px;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease;
}

.dashboard-image:hover {
  transform: scale(1.02);
}

@media (max-width: 768px) {
  .dashboard-illustration {
    margin: 1.5rem 0 2rem;
  }
}
</style>